import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";

type Candidate = {
  id: string;
  kind: "task" | "appointment";
  title: string;
  version: number;
  date: string | null;
  status: string;
};

type Props = {
  opportunityId: string;
  contactId?: string | null;
  organizationId?: string | null;
  readOnly?: boolean;
};

function items(response: any): any[] {
  return Array.isArray(response?.data?.items) ? response.data.items : [];
}

export const OpportunityActivityLinker: React.FC<Props> = ({
  opportunityId, contactId, organizationId, readOnly,
}) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const params = contactId ? { contact_id: contactId, limit: 500 }
      : organizationId ? { organization_id: organizationId, limit: 500 }
      : null;
    if (!params) { setCandidates([]); setLoading(false); return; }
    try {
      const [tasks, events] = await Promise.all([
        api.get("/crm/tasks", { params }), api.get("/crm/events", { params }),
      ]);
      const taskRows: Candidate[] = items(tasks)
        .filter((x) => !x.opportunity_id)
        .map((x) => ({ id: x.id, kind: "task", title: x.title, version: x.version,
          date: x.due_at || x.due_date || null, status: x.status }));
      const eventRows: Candidate[] = items(events)
        .filter((x) => !x.opportunity_id)
        .map((x) => ({ id: x.id, kind: "appointment", title: x.title, version: x.version,
          date: x.start_at || x.start_date || null, status: x.status }));
      setCandidates([...taskRows, ...eventRows].sort((a, b) => String(a.date || "").localeCompare(String(b.date || ""))));
      setSelected(new Set());
    } catch (e: any) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not load compatible activity.");
    } finally { setLoading(false); }
  }, [contactId, organizationId]);

  useEffect(() => { load(); }, [load]);
  const selectedRows = useMemo(() => candidates.filter((x) => selected.has(`${x.kind}:${x.id}`)), [candidates, selected]);

  async function attach() {
    if (!selectedRows.length) return;
    setSaving(true); setError(""); setMessage("");
    let completed = 0;
    try {
      for (const row of selectedRows) {
        const path = row.kind === "task" ? `/crm/tasks/${row.id}` : `/crm/events/${row.id}`;
        await api.patch(path, { if_version: row.version, opportunity_id: opportunityId });
        completed += 1;
      }
      setMessage(`${completed} item${completed === 1 ? "" : "s"} attached to this opportunity.`);
      await load();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const friendly = detail === "version_conflict"
        ? "One item changed while you were reviewing it. We refreshed the list; review it and try again."
        : detail === "linked_records_inconsistent"
          ? "One item is no longer compatible with this opportunity. Review its contact and clinic links."
          : formatApiError(detail);
      setError(`${completed ? `${completed} item(s) were attached. ` : ""}${friendly || "Could not attach the selected activity."}`);
      await load();
    } finally { setSaving(false); }
  }

  function toggle(key: string) {
    setSelected((current) => { const next = new Set(current); next.has(key) ? next.delete(key) : next.add(key); return next; });
  }

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4 md:p-5" data-testid="opportunity-activity-linker">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="font-semibold text-slate-900">Attach existing activity</h3>
          <p className="text-xs text-slate-500 mt-1">Review compatible unlinked tasks and appointments. Historical ownership and status will not change.</p></div>
        {!readOnly && candidates.length > 0 ? <Button size="sm" onClick={attach} disabled={saving || !selectedRows.length} data-testid="activity-link-attach-btn">
          {saving ? "Attaching…" : `Attach selected (${selectedRows.length})`}
        </Button> : null}
      </div>
      {loading ? <div className="text-sm text-slate-500 mt-4" role="status">Loading compatible activity…</div>
        : candidates.length === 0 ? <div className="text-sm text-slate-500 mt-4" data-testid="activity-link-empty">No compatible unlinked activity.</div>
        : <div className="mt-4 space-y-2">{candidates.map((row) => { const key = `${row.kind}:${row.id}`; return (
          <label key={key} className="flex items-start gap-3 rounded border border-slate-200 p-3 text-sm">
            <input type="checkbox" checked={selected.has(key)} disabled={readOnly || saving} onChange={() => toggle(key)} aria-label={`Select ${row.kind} ${row.title}`} />
            <span className="min-w-0"><span className="font-medium">{row.title}</span><span className="block text-xs text-slate-500 capitalize">{row.kind} · {row.status}{row.date ? ` · ${new Date(row.date).toLocaleString()}` : ""}</span></span>
          </label>); })}</div>}
      {message ? <div className="mt-3 text-sm text-emerald-700" role="status">{message}</div> : null}
      {error ? <div className="mt-3 text-sm text-red-600" role="alert">{error}</div> : null}
    </section>
  );
};
